import { useState, useEffect } from "react";
import { api } from "../api";
import { Card, Field, Input, Btn, Badge, REGIONS, Select } from "../components/Shared";
import StorageUsageCard from "./StorageUsageCard";

/**
 * Where this facility's data goes.
 *
 * It used to be configured per project, which meant the same bucket and the
 * same key retyped for every project — and any operator who could open any
 * project could read a credential that reaches every project sharing the
 * bucket. Group admin and above only, which is the whole reason it moved here.
 *
 * The secret is never sent back. The form asks only whether one is stored, so a
 * save that does not retype it keeps what is there; reading it back would put a
 * live credential on a screen and into a browser's memory for no benefit.
 */
export default function GroupStoragePanel({ groupId, canEdit }) {
  const [cfg, setCfg]     = useState(null);
  const [form, setForm]   = useState({});
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!groupId) return;
    setError(null);
    setSaved(false);
    api.get(`/api/groups/${groupId}/s3-config`)
      .then(c => {
        setCfg(c);
        setForm({ s3_bucket: c.s3_bucket || "", s3_region: c.s3_region || "",
                  s3_endpoint_url: c.s3_endpoint_url || "", s3_key_id: c.s3_key_id || "",
                  s3_secret: "", s3_root_prefix: c.s3_root_prefix || "" });
      })
      .catch(e => setError(e.message));
  }, [groupId]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await api.patch(`/api/groups/${groupId}/s3-config`, form);
      setCfg(updated);
      // The server normalises the root (a trailing "/" dropped), so show what it stored.
      setForm(f => ({ ...f, s3_secret: "", s3_root_prefix: updated.s3_root_prefix || "" }));
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return <Card><h3 style={{ margin: 0, fontSize: 16 }}>Storage</h3></Card>;

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Storage</h3>
        <Badge color={cfg.configured ? "green" : "gray"}>
          {cfg.configured ? "Configured" : "Not configured"}
        </Badge>
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
        Every project in this group uploads to and is delivered from this bucket. Each project gets
        its own folder, named after the project — that part is not configurable, which is what
        stops one project reading another's data. A root prefix, if set, puts the folders of new
        projects under it.
      </p>

      {error && <div role="alert" style={{ marginBottom: 12, color: "#dc2626", fontSize: 14 }}>{error}</div>}
      {saved && <div role="status" style={{ marginBottom: 12, color: "#065f46", fontSize: 14 }}>
        Saved. The bucket answered, so it is reachable with these credentials.
      </div>}

      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <Field label="Bucket" required>
          <Input value={form.s3_bucket} onChange={set("s3_bucket")} disabled={!canEdit} />
        </Field>
        {/* A list, not a text box. Typed free-hand every deployment invents its
            own spelling, and a region string that nothing accepts is discovered
            at the first upload. REGIONS has existed in Shared.jsx all along and
            was used nowhere. A stored value outside the list is added to it, so
            saving cannot silently rewrite a region somebody set deliberately. */}
        <Field label="Region" required>
          <Select value={form.s3_region} onChange={set("s3_region")} disabled={!canEdit}
                  options={[
                    ...(form.s3_region && !REGIONS.includes(form.s3_region)
                        ? [{ value: form.s3_region, label: `${form.s3_region} (stored)` }] : []),
                    ...REGIONS.map(r => ({ value: r, label: r })),
                  ]} />
        </Field>
        <Field label="Endpoint URL" required
               hint="This facility runs MinIO, so it is always set. Blank would mean real AWS.">
          <Input value={form.s3_endpoint_url} onChange={set("s3_endpoint_url")} disabled={!canEdit} />
        </Field>
        <Field label="Access key ID" required>
          <Input value={form.s3_key_id} onChange={set("s3_key_id")} disabled={!canEdit} />
        </Field>
        {/* Required only until one is stored: after that, blank means "keep the
            one you have", so demanding it would make every other edit on this
            form need the secret retyped. */}
        <Field
          label="Secret access key"
          required={!cfg.s3_secret_set}
          hint={cfg.s3_secret_set
            ? "A key is stored. Leave blank to keep it, or type a new one to replace it."
            : "Required."}
        >
          <Input type="password" value={form.s3_secret} onChange={set("s3_secret")}
                 placeholder={cfg.s3_secret_set ? "•••••••• (unchanged)" : ""}
                 disabled={!canEdit} autoComplete="new-password" />
        </Field>
        {/* Stamped on each project when it is created, so changing it moves
            nothing: a project keeps the root it was created under, and every
            feature looks for its data there. Blank means the bucket root. */}
        <Field label="S3 root prefix"
               hint="Applies to new projects only. Existing projects keep their layout and their data stays where it is. Blank means the bucket root.">
          <Input value={form.s3_root_prefix} onChange={set("s3_root_prefix")}
                 placeholder="e.g. project-portal" disabled={!canEdit} />
        </Field>
      </div>

      {canEdit && (
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <Btn onClick={save} disabled={busy}>{busy ? "Checking the bucket…" : "Save"}</Btn>
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            Saving checks the bucket answers first. A mistyped endpoint here breaks every project
            in the group at once, and would otherwise be found by the first researcher to upload —
            hours into a transfer.
          </span>
        </div>
      )}

      <StorageUsageCard groupId={groupId} canEdit={canEdit} />
    </Card>
  );
}
